import React, {useState, useEffect, useRef, useContext} from 'react';
import styles from './Account.module.css';
import {PRISMAX_BACKEND_URL} from '../../configs/config';

// Import avatar images
import defaultAvatar from '../../static_files/badge_explorer.svg';
import amplifierAvatar from '../../static_files/badge_amplifier.svg';
import innovatorAvatar from '../../static_files/badge_innovator.svg';
import checkCircle from '../../static_files/check-circle-1.svg';
import checkCircleGreen from '../../static_files/check-circle-green.svg';

import UpgradeMembershipModal from "../Fleet/UpgradeMembershipModal";
import {SwapMainAppContext} from "../../context/SwapMainAppContext";
import {shortenAddress} from "../../utils/utils";

const Account = () => {
    const [userInfo, setUserInfo] = useState({
        userName: '',
        email: '',
        telegramId: '',
        twitterId: '',
        phoneNumber: ''
    });

    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [editedInfo, setEditedInfo] = useState({});
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);

    // Refs for input fields
    const userNameRef = useRef(null);
    const emailRef = useRef(null);
    const telegramRef = useRef(null);
    const twitterRef = useRef(null);
    const phoneRef = useRef(null);
    const {
        userClass,
        setUserClass,
        walletAddress,
    } = useContext(SwapMainAppContext);
    useEffect(() => {
        fetchUserInfo();
    }, []);

    const fetchUserInfo = async () => {
        try {
            setIsLoading(true);

            // Get user identifier and token
            const userEmail = localStorage.getItem('userEmail');
            const walletAddress = localStorage.getItem('walletAddress');
            const token = localStorage.getItem('gatewayToken');

            if (!userEmail && !walletAddress) {
                setIsLoading(false);
                return;
            }

            if (!token) {
                console.error('No authentication token found');
                setIsLoading(false);
                return;
            }

            // Build query params
            const params = new URLSearchParams();
            if (userEmail) {
                params.append('email', userEmail);
            } else if (walletAddress) {
                params.append('wallet_address', walletAddress);
            }

            // Fetch user info from backend
            const response = await fetch(`${PRISMAX_BACKEND_URL}/api/get-users?${params}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const result = await response.json();
                if (result.success && result.data) {
                    const data = result.data;
                    const userClassType = data.user_class || 'Explorer Member';

                    setUserInfo({
                        userName: data.user_name || '--',
                        email: data.email || '--',
                        telegramId: data.telegram_id || '--',
                        twitterId: data.twitter_id || '--',
                        phoneNumber: data.phone_number || '--'
                    });
                    setUserClass(userClassType);
                    setEditedInfo({
                        userName: data.user_name || '--',
                        email: data.email || '--',
                        telegramId: data.telegram_id || '--',
                        twitterId: data.twitter_id || '--',
                        phoneNumber: data.phone_number || '--'
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching user info:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = () => {
        if (isEditing) {
            // Save changes
            saveUserInfo();
        } else {
            // Enter edit mode
            setIsEditing(true);
            setTimeout(() => {
                userNameRef.current?.focus();
            }, 50);
        }
    };

    const saveUserInfo = async () => {
        try {
            const userEmail = localStorage.getItem('userEmail');
            const walletAddress = localStorage.getItem('walletAddress');
            const token = localStorage.getItem('gatewayToken');

            if (!token) {
                console.error('No authentication token found');
                return;
            }

            const response = await fetch(`${PRISMAX_BACKEND_URL}/api/update-user-info`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    ...(userEmail ? {email: userEmail} : {wallet_address: walletAddress}),
                    user_name: editedInfo.userName,
                    // update_email: editedInfo.email,
                    telegram_id: editedInfo.telegramId,
                    twitter_id: editedInfo.twitterId,
                    phone_number: editedInfo.phoneNumber
                })
            });

            if (response.ok) {
                const result = await response.json();
                if (result.success) {
                    setUserInfo(editedInfo);
                    setIsEditing(false);
                }
            }
        } catch (error) {
            console.error('Error updating user info:', error);
        }
    };

    const handleInputChange = (field, value) => {
        setEditedInfo(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const getAvatarImage = () => {
        switch (userClass) {
            case 'Amplifier Member':
                return amplifierAvatar;
            case 'Innovator Member':
                return innovatorAvatar;
            default:
                return defaultAvatar;
        }
    };
    const handleOpenUpgradeModal = () => {
        setShowUpgradeModal(true);
    };

    const handleCloseUpgradeModal = (upgradeSucceeded = false) => {
        setShowUpgradeModal(false);
        if (upgradeSucceeded) {
            fetchUserInfo();
        }
    };
    const renderLoadingBox = () => (
        <div className={styles.loadingBox}>
            <div className={styles.loadingAvatar}/>
            <div className={styles.loadingTitle}/>
            <div className={styles.loadingContent}>
                <div className={styles.loadingLine}/>
                <div className={styles.loadingLine}/>
                <div className={styles.loadingLine}/>
            </div>
        </div>
    );

    const renderPersonalInfo = () => (
        <div className={styles.infoBox}>
            <div className={styles.infoHeader}>
                <h3>Personal info</h3>
                <button
                    className={styles.editButton}
                    onClick={handleEdit}
                >
                    {isEditing ? 'Save' : 'Edit'}
                </button>
            </div>

            <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                    <label>User name</label>
                    {isEditing ? (
                        <input
                            ref={userNameRef}
                            type="text"
                            value={editedInfo.userName}
                            onChange={(e) => handleInputChange('userName', e.target.value)}
                            className={styles.infoInput}
                        />
                    ) : (
                        <span>{userInfo.userName}</span>
                    )}
                </div>

                <div className={styles.infoItem}>
                    <label>Email</label>
                    {isEditing && userInfo.email === '--' ? (
                        <input
                            value={editedInfo.email}
                            type="email"
                            style={{cursor: 'pointer'}}
                            onClick={() => window.open('https://gateway.prismax.ai', '_blank', 'noopener,noreferrer')}
                            className={`${styles.infoInput} ${styles.infoInputEmail}`}
                        />
                    ) : (
                        <span>{userInfo.email}</span>
                    )}
                </div>

                <div className={styles.infoItem}>
                    <label>Telegram ID</label>
                    {isEditing ? (
                        <input
                            ref={telegramRef}
                            type="text"
                            value={editedInfo.telegramId}
                            onChange={(e) => handleInputChange('telegramId', e.target.value)}
                            className={styles.infoInput}
                        />
                    ) : (
                        <span>{userInfo.telegramId}</span>
                    )}
                </div>

                <div className={styles.infoItem}>
                    <label>Twitter ID</label>
                    {isEditing ? (
                        <input
                            ref={twitterRef}
                            type="text"
                            value={editedInfo.twitterId}
                            onChange={(e) => handleInputChange('twitterId', e.target.value)}
                            className={styles.infoInput}
                        />
                    ) : (
                        <span>{userInfo.twitterId}</span>
                    )}
                </div>

                <div className={styles.infoItem}>
                    <label>Phone Number</label>
                    {isEditing ? (
                        <input
                            ref={phoneRef}
                            type="tel"
                            value={editedInfo.phoneNumber}
                            onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                            className={styles.infoInput}
                        />
                    ) : (
                        <span>{userInfo.phoneNumber}</span>
                    )}
                </div>
            </div>
        </div>
    );

    const renderMembershipBox = (type, features, price, isCurrent) => {
        const membershipClasses = {
            Explorer: styles.explorerBox,
            Amplifier: styles.amplifierBox,
            Innovator: styles.innovatorBox
        };

        return (
            <div className={`${styles.membershipBox} ${membershipClasses[type]}`}>
                <div className={styles.membershipBoxInner}>
                    <div className={styles.membershipHeader}>
                        <h3 style={{
                            color: isCurrent ?
                                (type === 'Explorer' ? '#6CE3C4' :
                                    type === 'Amplifier' ? '#FFC786' :
                                        type === 'Innovator' ? '#E11FFF' : '') :
                                '#FFFFFF'
                        }}>{type} Member</h3>
                    </div>

                    {price && (type !== 'Innovator' || userClass === 'Amplifier Member') && (
                        <div className={styles.price}>
                            ${price} <span className={styles.priceNote}>(one-time initiation fee)</span>
                        </div>
                    )}

                    <ul className={styles.featuresList}>
                        {features.map((feature, index) => (
                            <li key={index}>
                                <img
                                    src={isCurrent ? checkCircleGreen : checkCircle}
                                    alt='✓'
                                />
                                {feature}
                            </li>
                        ))}
                    </ul>


                </div>
                {isCurrent && userClass !== 'Innovator Member' && (
                    <span
                        className={styles.currentBadge}
                        style={userClass === 'Explorer Member' ? {borderColor: '#275340'} : {}}
                    >
                        Current membership
                    </span>)}
                {!isCurrent && (type !== 'Innovator' || userClass === 'Amplifier Member') && (
                    <button
                        className={styles.upgradeButton}
                        onClick={() => handleOpenUpgradeModal()}
                    >
                        Upgrade
                    </button>
                )}

            </div>
        );
    };

    return (
        <div className={styles.accountContainer}>
            <div className={styles.accountContent}>
                {!isLoading && (<div className={styles.profileSection}>
                    <img
                        src={getAvatarImage()}
                        alt="User Avatar"
                        className={styles.avatar}
                    />
                    <h2 className={styles.userName}>{walletAddress ? shortenAddress(walletAddress) : '--'}</h2>
                </div>)}


                {isLoading ? (
                    renderLoadingBox()
                ) : (
                    <>
                        {renderPersonalInfo()}

                        <div className={styles.membershipSection}>
                            {userClass === 'Explorer Member' && (
                                <>
                                    {renderMembershipBox(
                                        'Explorer',
                                        ['Contribute and earn Prisma points'],
                                        null,
                                        true
                                    )}
                                    {renderMembershipBox(
                                        'Amplifier',
                                        ['Robot fleet access', 'Enhanced Prisma earning (3x speed)'],
                                        99,
                                        false
                                    )}
                                    {renderMembershipBox(
                                        'Innovator',
                                        ['Teleop tech suite access', 'Full AI model dataset library', 'Custom robotics AI model'],
                                        999,
                                        false
                                    )}
                                </>
                            )}

                            {userClass === 'Amplifier Member' && (
                                <>
                                    {renderMembershipBox(
                                        'Amplifier',
                                        ['Contribute and earn Prisma points', 'Robot fleet access', 'Enhanced Prisma earning (3x speed)'],
                                        null,
                                        true
                                    )}
                                    {renderMembershipBox(
                                        'Innovator',
                                        ['Teleop tech suite access', 'Full AI model dataset library', 'Custom robotics AI model'],
                                        999,
                                        false
                                    )}
                                </>
                            )}

                            {userClass === 'Innovator Member' && (
                                renderMembershipBox(
                                    'Innovator',
                                    ['Contribute and earn Prisma points', 'Robot fleet access', 'Enhanced Prisma earning (3x speed)', 'Teleop tech suite access', 'Full AI model dataset library', 'Custom robotics AI model'],
                                    null,
                                    true
                                )
                            )}
                        </div>
                    </>
                )}
            </div>
            {showUpgradeModal && (
                <UpgradeMembershipModal
                    onClose={handleCloseUpgradeModal}
                    onGoBack={() => setShowUpgradeModal(false)}
                />
            )}
        </div>
    );
};

export default Account;